import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

/**
 * Free-text combobox: a text input that accepts arbitrary input but offers a
 * styled suggestion dropdown of common values. Users can pick a suggestion or
 * type their own.
 *
 * Unlike a native <datalist>, the dropdown here is fully styled to match the
 * design system (rounded-xl panel, shadow-lg, hover/selected states) — the
 * same panel pattern used by SearchableCombobox.
 *
 * The suggestion menu is always portaled (modal-safe) — same contract as
 * FormSelect / DatePickerField (createPortal + z-index 10002).
 *
 * Use this for fields that have frequent, well-known values but must still
 * accept arbitrary input (brand, CPU, OS version, warranty provider, etc.).
 * For closed value sets, use <FormSelect> instead.
 *
 * Admin (red) vs employee (blue) accent is inferred from the className
 * (anything containing "admin" → red), matching the field's input class.
 *
 * @example
 * <DatalistCombobox
 *   value={brand}
 *   onChange={setBrand}
 *   suggestions={['Dell', 'HP', 'Lenovo']}
 *   placeholder="Select or type a brand"
 * />
 */
const DatalistCombobox = ({
  value,
  onChange,
  suggestions = [],
  placeholder,
  className = 'input-admin w-full',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);

  const isAdmin = className.includes('admin');

  const options = useMemo(
    () => [...new Set(suggestions.filter(Boolean).map(String))],
    [suggestions]
  );

  // Filter suggestions against the typed value, but keep the full list visible
  // when the current value exactly matches a suggestion (so users can browse
  // alternatives after a pick).
  const filtered = useMemo(() => {
    const term = String(value ?? '').trim().toLowerCase();
    if (!term || options.some((opt) => opt.toLowerCase() === term)) return options;
    return options.filter((opt) => opt.toLowerCase().includes(term));
  }, [options, value]);

  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownMaxHeight = 240; // max-h-60
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
    const width = Math.min(rect.width, viewportWidth - 16);
    const left = Math.max(8, Math.min(rect.left, viewportWidth - width - 8));

    setDropdownStyle({
      position: 'fixed',
      left,
      width,
      // Above sticky modal header/footer (10001) — same as DatePickerField
      zIndex: 10002,
      ...(openUpward
        ? { bottom: viewportHeight - rect.top + 4, maxHeight: Math.min(spaceAbove, dropdownMaxHeight) }
        : { top: rect.bottom + 4, maxHeight: Math.min(spaceBelow, dropdownMaxHeight) }),
    });
  }, []);

  // Click-outside that accounts for the portaled menu node.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    setHighlightIdx(-1);
  }, [filtered]);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIdx];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const showMenu = isOpen && !disabled && filtered.length > 0;

  useEffect(() => {
    if (!showMenu) return undefined;
    updateDropdownPosition();
    const update = () => updateDropdownPosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showMenu, updateDropdownPosition, filtered.length]);

  const select = (opt) => {
    onChange(opt);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    const count = filtered.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      if (count) setHighlightIdx((prev) => (prev + 1) % count);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (count) setHighlightIdx((prev) => (prev - 1 + count) % count);
    } else if (e.key === 'Enter') {
      if (isOpen && highlightIdx >= 0 && filtered[highlightIdx]) {
        e.preventDefault();
        select(filtered[highlightIdx]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const portaledMenu = showMenu && typeof document !== 'undefined' && createPortal(
    <div
      ref={menuRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden flex flex-col"
    >
      <div className="overflow-y-auto min-h-0 flex-1" ref={listRef}>
        {filtered.map((opt, idx) => {
          const isSelected = String(value ?? '') === opt;
          const isHighlighted = highlightIdx === idx;
          return (
            <button
              key={opt}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                select(opt);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                isSelected
                  ? isAdmin
                    ? 'bg-red-50 text-red-700 font-medium'
                    : 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-gray-700'
              } ${isHighlighted ? 'bg-gray-100' : ''}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );

  return (
    <div className="relative" ref={ref}>
      <input
        ref={inputRef}
        type="text"
        value={value ?? ''}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className={`${className} pr-10`}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      <ChevronDown
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${
          isOpen ? 'rotate-180' : ''
        }`}
      />
      {portaledMenu}
    </div>
  );
};

export default DatalistCombobox;
